import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { nanoid } from 'nanoid';

import { MementoService } from '@ui-furniture/common/memento/memento-service';
import { CreateMemento } from '@ui-furniture/common/memento/types/create-memento';

import { ActiveInput } from '../types/active-input';
import { Message } from '../types/message';

export const useChatState = ({
  initialMessages = [],
  mementoService = new MementoService(),
}: {
  initialMessages?: Array<Message>;
  mementoService?: MementoService;
} = {}) => {
  const [messages, setStateMessages] = useState(initialMessages);
  const latestMessages = useRef(messages);

  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [input, internalSetInput] = useState<ActiveInput | null>(null);
  const setInput = useCallback((newInput: ReactNode) => {
    internalSetInput({
      id: nanoid(),
      input: newInput,
    });
  }, []);
  const resetInput = useCallback(() => {
    internalSetInput(null);
  }, []);
  const pushMessages = useCallback((newMessages: Array<Message>) => {
    setStateMessages((currentMessages) => {
      const updatedMessages = [...currentMessages, ...newMessages];
      latestMessages.current = updatedMessages;
      return updatedMessages;
    });
  }, []);
  const pushBotMessage = useCallback((message: string) => {
    pushMessages([{ text: message, profile: 'bot' }]);
  }, []);
  const processUserResponse = useCallback((text: string) => {
    const snapshot = mementoService.saveSnapshot();

    pushMessages([{ text, profile: 'user', restore: snapshot.restore }]);
    resetInput();
  }, []);

  const createMemento = useCallback<CreateMemento>(() => {
    const savedState = {
      currentMessages: [...latestMessages.current],
    };
    return {
      restore: () => {
        latestMessages.current = savedState.currentMessages;
        setStateMessages(savedState.currentMessages);
        setIsEditing(false);
      },
    };
  }, []);

  useMemo(() => {
    mementoService.registerMementoCreator(createMemento);
  }, []);
  const undo = mementoService.restorePrevious;

  return {
    controls: {
      setInput,
      pushBotMessage,
      processUserResponse,
    },
    internalState: {
      pushMessages,
      setIsTyping,
      setIsEditing,
      undo,
      isTyping,
      isEditing,
      input,
      messages,
    },
  };
};
